import { Link } from "react-router-dom";
import { MapPin, Ruler, ShieldCheck, CheckCircle2, Sparkles, Info } from "lucide-react";
import { gbp, floorLabel, priceBasisLabel } from "@/lib/api";
import { SavingLine } from "@/components/SavingLine";
import { resolveListingImages } from "@/lib/categoryImages";
import { useUnit } from "@/context/UnitContext";
import { dimPairLabel } from "@/lib/units";

export default function ListingCard({ l }) {
  const { unit } = useUnit();
  const isPro = !!l.retailer?.is_pro;
  const { photos, isFallback, alt } = resolveListingImages(l);
  const primaryPhoto = photos[0];
  return (
    <Link
      to={`/listing/${l.id}`}
      className={`group block bg-white border rounded-xl overflow-hidden hover:-translate-y-0.5 hover:shadow-md transition-transform ${
        isPro ? "border-[#E05915]/40 shadow-sm ring-1 ring-[#E05915]/10" : "border-slate-200"
      }`}
      data-testid={`listing-card-${l.reference_code}`}
    >
      <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden">
        {primaryPhoto && (
          <img
            src={primaryPhoto}
            alt={alt}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        )}
        {isPro && (
          <span
            className="absolute top-3 left-3 inline-flex items-center gap-1 bg-gradient-to-r from-[#E05915] to-[#c24b10] text-white text-[11px] font-bold px-2.5 py-1 rounded-full shadow uppercase tracking-wide"
            data-testid="featured-badge"
          >
            <Sparkles className="w-3 h-3" /> Featured
          </span>
        )}
        {isFallback && (
          <span
            className="absolute bottom-3 right-3 inline-flex items-center gap-1 bg-slate-900/70 backdrop-blur-sm text-white text-[10px] font-semibold px-2 py-1 rounded-full uppercase tracking-wide"
            data-testid="illustrative-image-badge"
          >
            <Info className="w-3 h-3" /> Illustrative image
          </span>
        )}
        {l.fits === true && (
          <span
            className="absolute top-3 right-3 inline-flex items-center gap-1 bg-[#2A5A3B] text-white text-xs font-bold px-2.5 py-1 rounded-full shadow"
            data-testid={l.matching_mode === "coverage" ? "enough-badge" : "fits-badge"}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />{" "}
            {l.matching_mode === "coverage" ? "Enough for your room" : "Fits your room"}
          </span>
        )}
        <span className="absolute bottom-3 left-3 bg-white/95 text-slate-800 text-xs font-medium px-2 py-1 rounded-full capitalize">
          {floorLabel(l.flooring_type)}
        </span>
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display font-semibold text-slate-900 capitalize leading-tight">
            {l.colour} {floorLabel(l.flooring_type)}
          </h3>
          <div className="text-right shrink-0">
            <span className="text-lg font-display font-bold text-slate-900">{gbp(l.price_gbp)}</span>
            <span className="block text-[11px] text-slate-500" data-testid="card-price-basis">{priceBasisLabel(l)}</span>
          </div>
        </div>
        <SavingLine price={l.price_gbp} retail={l.rrp_gbp} testId={`card-saving-${l.id}`} />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
          {l.matching_mode === "coverage" ? (
            <>
              <span className="flex items-center gap-1">
                <Ruler className="w-3.5 h-3.5" aria-hidden="true" />
                {l.total_coverage_m2}m² available
              </span>
              {l.coverage_match?.packs_required != null && (
                <>
                  <span>·</span>
                  <span>
                    {l.coverage_match.packs_required} of {l.packs_available} packs required
                  </span>
                </>
              )}
            </>
          ) : (
            <>
              <span className="flex items-center gap-1">
                <Ruler className="w-3.5 h-3.5" aria-hidden="true" />
                {dimPairLabel(l.width_m, l.length_m, unit)}
              </span>
              <span>·</span>
              <span>{l.area_m2}m²</span>
            </>
          )}
        </div>
        {l.matching_mode === "coverage" && l.price_basis === "per_pack" && (
          <div className="text-[11px] text-slate-500">
            {gbp(l.price_gbp)} per pack · approx. {gbp((l.price_gbp || 0) * (l.packs_available || 0))} for all stock
          </div>
        )}
        {l.fits === true && l.fits_rotated && (
          <div className="text-[11px] text-[#2A5A3B] font-semibold flex items-center gap-1" data-testid="fits-rotated-note">
            <CheckCircle2 className="w-3 h-3" /> Fits when rotated
          </div>
        )}
        <div className="flex items-center gap-1.5 text-xs text-slate-500 pt-1">
          <ShieldCheck className="w-3.5 h-3.5 text-[#2A5A3B]" aria-hidden="true" />
          <span className="truncate">Verified retailer · {l.retailer.locality}</span>
          {l.distance_label && (
            <>
              <span>·</span>
              <span className="font-medium text-slate-700">{l.distance_label}</span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}
